import { useState, useEffect, useRef } from "react";
import { Search, Star, MapPin, Tag, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { loadGoogleMapsScript } from "@/lib/googleMaps";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { BusinessHourDto } from "@/services/businessService";

// Extended Business interface to carry all data needed for YourBusiness page
export interface Business {
  id: string; // place_id
  name: string;
  address: string;
  rating: number;
  reviewCount: number;
  isOpen: boolean;
  categories: string[];
  thumbnail: string;
  
  // Extra fields for auto-fill
  phone?: string;
  website?: string;
  hours?: Record<string, BusinessHourDto>;
  location?: { lat: number; lng: number };
}

interface StepBusinessSearchProps {
  onSelect: (business: Business) => void;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

export const StepBusinessSearch = ({ onSelect }: StepBusinessSearchProps) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // 500ms debounce
  const [results, setResults] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn("Google Maps API Key is missing. Search will utilize mock data if available or fail.");
      return;
    }

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        setScriptLoaded(true);
        // PlacesService requires a node, even if we don't render results to it automatically
        placesService.current = new google.maps.places.PlacesService(document.createElement('div'));
      })
      .catch((err) => {
        console.error("Failed to load Google Maps script", err);
        toast({
          title: "Error",
          description: "Failed to load Google Maps. Please refresh and try again.",
          variant: "destructive"
        });
      });
  }, [toast]);

  useEffect(() => {
    if (!debouncedSearchQuery || debouncedSearchQuery.length < 3) {
      setResults([]);
      return;
    }

    if (!scriptLoaded || !placesService.current) {
        return; 
    }

    setLoading(true);
    // Use textSearch instead of AutocompleteService to avoid deprecated API and get richer results
    const request: google.maps.places.TextSearchRequest = {
        query: debouncedSearchQuery,
        type: 'establishment'
    };

    placesService.current.textSearch(request, (places, status) => {
        setLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && places) {
            const mappedResults: Business[] = places.map(p => ({
                id: p.place_id || "",
                name: p.name || "",
                address: p.formatted_address || "",
                rating: p.rating || 0,
                reviewCount: p.user_ratings_total || 0,
                isOpen: p.opening_hours?.open_now || false, // Note: open_now is deprecated but still commonly available in legacy PlacesService
                categories: p.types || [],
                thumbnail: p.photos?.[0]?.getUrl({ maxWidth: 400 }) || "/placeholder.svg",
                location: p.geometry?.location ? { lat: p.geometry.location.lat(), lng: p.geometry.location.lng() } : undefined
            }));
            setResults(mappedResults);
        } else {
            setResults([]);
        }
    });
  }, [debouncedSearchQuery, scriptLoaded]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const mapGoogleHours = (periods: google.maps.places.PlaceOpeningHoursPeriod[]): Record<string, BusinessHourDto> => {
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const mapping: Record<string, BusinessHourDto> = {};

      // Initialize all days as closed
      days.forEach(day => {
          mapping[day] = { enabled: false, open: "09:00", close: "17:00" };
      });

      if (!periods) return mapping;

      periods.forEach(p => {
          if (p.open && p.open.day !== undefined) {
              const dayName = days[p.open.day];
              
              const formatTime = (hours: number, minutes: number) => {
                  const h = hours.toString().padStart(2, '0');
                  const m = minutes.toString().padStart(2, '0');
                  return `${h}:${m}`;
              };

              // Handle 24/7 or closing time
              if (!p.close) {
                  // Assuming 24h
                  mapping[dayName] = {
                      enabled: true,
                      open: "00:00",
                      close: "23:59"
                  };
              } else {
                  mapping[dayName] = {
                      enabled: true,
                      open: formatTime(p.open.hours, p.open.minutes),
                      close: formatTime(p.close.hours, p.close.minutes)
                  };
              }
          }
      });
      return mapping;
  };

  const handleSelect = (basicBusiness: Business) => {
      if (!placesService.current) return;

      setLoading(true);
      // Fetch full details (especially opening_hours periods which might not be in textSearch results fully)
      placesService.current.getDetails(
          { 
              placeId: basicBusiness.id,
              fields: ['name', 'formatted_address', 'formatted_phone_number', 'website', 'rating', 'user_ratings_total', 'opening_hours', 'photos', 'types', 'geometry']
          },
          (place, status) => {
              setLoading(false);
              if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                  const detailedBusiness: Business = {
                      ...basicBusiness,
                      name: place.name || basicBusiness.name,
                      address: place.formatted_address || basicBusiness.address,
                      rating: place.rating || basicBusiness.rating,
                      reviewCount: place.user_ratings_total || basicBusiness.reviewCount,
                      // opening_hours.isOpen() is available in the object returned by getDetails
                      isOpen: place.opening_hours?.isOpen?.() || false,
                      thumbnail: place.photos?.[0]?.getUrl({ maxWidth: 400 }) || basicBusiness.thumbnail,
                      phone: place.formatted_phone_number,
                      website: place.website,
                      hours: place.opening_hours?.periods ? mapGoogleHours(place.opening_hours.periods) : undefined,
                      location: place.geometry?.location ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } : basicBusiness.location
                  };
                  onSelect(detailedBusiness);
              } else {
                  // Fallback if details fail, return what we have from textSearch
                  onSelect(basicBusiness);
              }
          }
      );
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Find your Google Business Profile</h1>
        <p className="text-muted-foreground">
          Search for your business to get started with Sophie AI
        </p>
      </div>

      <div className="relative max-w-2xl mx-auto">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search for your business..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-12 h-14 text-lg rounded-xl shadow-md"
        />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-muted-foreground" />}
      </div>

      {results.length > 0 && (
        <div className="space-y-3 max-w-2xl mx-auto">
          {results.map((business) => (
            <button
              key={business.id}
              onClick={() => handleSelect(business)}
              className="w-full p-4 bg-card rounded-xl border border-border hover:border-primary hover:shadow-lg transition-all text-left"
            >
              <div className="flex gap-4">
                {/* We use a placeholder or generic icon for search results as we don't have photos yet usually */}
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold text-lg">{business.name}</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="line-clamp-1">{business.address}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {searchQuery.length > 2 && results.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No businesses found. Try a different search.</p>
        </div>
      )}
    </div>
  );
};
